/**
 * ServiceCataloguePage — `/franchise/services`.
 *
 * Backed by the dedicated `/v1/franchise/service-catalogue` surface — each
 * service has a description, fee, eligible tiers and JSON rules. The matrix
 * is derived from the catalogue rather than from `tier.allowedServices`.
 */
import { useMemo, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  listServiceCatalogue,
  listTiers,
  type ServiceCatalogueEntry,
  type TierCode,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

const TIERS: readonly TierCode[] = ['TIER_1', 'TIER_2', 'TIER_3'];

export function ServiceCataloguePage(): JSX.Element {
  const [activeOnly, setActiveOnly] = useState(true);

  const services = useQuery({
    queryKey: ['franchise', 'service-catalogue', 'list', activeOnly],
    queryFn: () => listServiceCatalogue({ active: activeOnly ? true : undefined }),
    retry: false,
    staleTime: 60_000,
  });

  const tiers = useQuery({
    queryKey: ['franchise', 'service-catalogue', 'tiers'],
    queryFn: () => listTiers({ active: true }),
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    (services.isError && services.error instanceof ApiError &&
      (services.error.status === 401 || services.error.status === 403)) ||
    (tiers.isError && tiers.error instanceof ApiError &&
      (tiers.error.status === 401 || tiers.error.status === 403));

  const items: readonly ServiceCatalogueEntry[] = useMemo(
    () => services.data?.items ?? [],
    [services.data],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Service catalogue"
        description="Per-tier service matrix sourced from the dedicated catalogue endpoint."
        actions={
          <label className="text-xs flex items-center gap-2">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Active only
          </label>
        }
      />

      {services.isPending || tiers.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseServiceCatalogue/read</code> required.
        </DeferredNotice>
      ) : services.isError ? (
        <DeferredNotice title="Service unreachable">{(services.error as Error).message}</DeferredNotice>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Catalogue empty.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Service</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-right px-4 py-2">Fee (NGN)</th>
                {TIERS.map((t) => (
                  <th key={t} className="text-center px-4 py-2">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50" data-testid="service-row">
                  <td className="px-4 py-2 font-mono text-xs">{s.serviceCode}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className="block font-medium text-gray-900">{s.displayName}</span>
                    <span className="text-[11px] text-muted">{s.description}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-700">{s.category}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">₦{s.fee}</td>
                  {TIERS.map((t) => (
                    <td key={t} className="px-4 py-2 text-center text-xs">
                      {Array.isArray(s.eligibleTiers) && s.eligibleTiers.includes(t) ? (
                        <span className="text-nipost-green">✓</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
